import { TournamentMatchStatusEnum } from '../types/enums';
import { IMatch } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';

const BEST_OF_N = 1;
const ALL_MATCHES_TIEBREAK = 0;

type CellChild = {
  status?: number;
  isTiebreaker?: boolean;
  tiebreakerPending?: boolean;
  results?: { homeTeam: number; awayTeam: number }[];
};

export type BracketCellSupersededExplanation = {
  winnerName?: string;
  homeWins: number;
  awayWins: number;
  matchCount: number;
  kind?: 'winner' | 'drawn';
};

function findTiebreaker(children: CellChild[]) {
  return children.find((m) => m.isTiebreaker);
}

function getNonDeciderChildren(children: CellChild[]) {
  return children.filter((m) => !m.isTiebreaker);
}

function countCategoryWins(children: CellChild[]) {
  let homeWins = 0;
  let awayWins = 0;
  for (const c of getNonDeciderChildren(children)) {
    if (c.status !== TournamentMatchStatusEnum.FINISHED) continue;
    const homeMatchWins = (c.results ?? []).filter((r) => r.homeTeam > r.awayTeam).length;
    const awayMatchWins = (c.results ?? []).filter((r) => r.awayTeam > r.homeTeam).length;
    if (homeMatchWins > awayMatchWins) homeWins++;
    else if (awayMatchWins > homeMatchWins) awayWins++;
  }
  return { homeWins, awayWins };
}

function countSeriesWinsExcludingPendingDecider(children: CellChild[]) {
  let homeSeriesWins = 0;
  let awaySeriesWins = 0;
  for (const c of children) {
    if (
      c.isTiebreaker &&
      c.tiebreakerPending &&
      c.status !== TournamentMatchStatusEnum.FINISHED
    ) {
      continue;
    }
    if (c.status !== TournamentMatchStatusEnum.FINISHED) continue;
    const homeMatchWins = (c.results ?? []).filter((r) => r.homeTeam > r.awayTeam).length;
    const awayMatchWins = (c.results ?? []).filter((r) => r.awayTeam > r.homeTeam).length;
    if (homeMatchWins > awayMatchWins) homeSeriesWins++;
    else if (awayMatchWins > homeMatchWins) awaySeriesWins++;
  }
  return { homeSeriesWins, awaySeriesWins };
}

function getCategorySlotCountForBestOfN(cellMatchCount: number, children: CellChild[]) {
  const nonDbChildren = getNonDeciderChildren(children);
  if (findTiebreaker(children)) {
    return Math.max(nonDbChildren.length, cellMatchCount - 1);
  }
  if (cellMatchCount === nonDbChildren.length + 1) {
    return nonDbChildren.length;
  }
  return cellMatchCount;
}

function resolveWinnerTeamId(
  cellMatchCount: number,
  children: CellChild[],
  homeId: number,
  awayId: number,
  winnerTeamId?: number | null,
): number | null {
  if (typeof winnerTeamId === 'number') {
    return winnerTeamId;
  }
  const categoryCount = getCategorySlotCountForBestOfN(cellMatchCount, children);
  const winsNeeded = Math.floor(categoryCount / 2) + 1;
  const { homeSeriesWins, awaySeriesWins } = countSeriesWinsExcludingPendingDecider(children);
  if (homeSeriesWins >= winsNeeded) return homeId;
  if (awaySeriesWins >= winsNeeded) return awayId;
  return null;
}

function getAllMatchesSupersededExplanation(
  cellMatches: IMatch[],
  match: IMatch,
): BracketCellSupersededExplanation | null {
  const homeId = match.homeTeam?.id;
  const awayId = match.awayTeam?.id;
  if (homeId == null || awayId == null) return null;

  const matchCount = match.bracketCellMatchCount ?? cellMatches.length;
  const sorted = [...cellMatches].sort(
    (a, b) => (a.bracketCellMatchIndex ?? 0) - (b.bracketCellMatchIndex ?? 0),
  );
  const { homeWins, awayWins } = countCategoryWins(sorted);

  if (match.bracketCellProgressionExplanation?.kind === 'DRAWN_MEETING') {
    return {
      homeWins,
      awayWins,
      matchCount,
      kind: 'drawn',
    };
  }

  if (homeWins === awayWins) return null;

  const winnerId = homeWins > awayWins ? homeId : awayId;
  const winnerName =
    winnerId === homeId ? match.homeTeam?.name ?? '' : match.awayTeam?.name ?? '';
  if (!winnerName) return null;

  return {
    winnerName,
    homeWins,
    awayWins,
    matchCount,
    kind: 'winner',
  };
}

export function getBestOfNSupersededExplanation(
  cellMatches: IMatch[],
  match: IMatch,
): BracketCellSupersededExplanation | null {
  if (match.bracketCellMatchSuperseded !== true) return null;
  if (match.bracketCellProgressionMode !== BEST_OF_N) return null;

  const homeId = match.homeTeam?.id;
  const awayId = match.awayTeam?.id;
  if (homeId == null || awayId == null) return null;

  const matchCount = match.bracketCellMatchCount ?? cellMatches.length;
  const sorted = [...cellMatches].sort(
    (a, b) => (a.bracketCellMatchIndex ?? 0) - (b.bracketCellMatchIndex ?? 0),
  );

  const winnerId = resolveWinnerTeamId(
    matchCount,
    sorted,
    homeId,
    awayId,
    match.bracketCellWinnerTeamId,
  );
  if (winnerId == null) return null;

  const winnerName =
    winnerId === homeId ? match.homeTeam?.name ?? '' : match.awayTeam?.name ?? '';
  if (!winnerName) return null;

  const { homeSeriesWins, awaySeriesWins } = countSeriesWinsExcludingPendingDecider(sorted);

  return {
    winnerName,
    homeWins: homeSeriesWins,
    awayWins: awaySeriesWins,
    matchCount,
    kind: 'winner',
  };
}

export function getBracketCellSupersededExplanation(
  cellMatches: IMatch[],
  match: IMatch,
): BracketCellSupersededExplanation | null {
  if (match.bracketCellMatchSuperseded !== true) return null;
  if (match.bracketCellProgressionMode === BEST_OF_N) {
    return getBestOfNSupersededExplanation(cellMatches, match);
  }
  if (match.bracketCellProgressionMode === ALL_MATCHES_TIEBREAK) {
    return getAllMatchesSupersededExplanation(cellMatches, match);
  }
  return null;
}

export function isTiebreakerRequired(
  cellMatches: IMatch[],
  match: IMatch,
): boolean {
  if (!match.isTiebreaker || match.bracketCellMatchSuperseded) return false;
  const sorted = [...cellMatches].sort(
    (a, b) => (a.bracketCellMatchIndex ?? 0) - (b.bracketCellMatchIndex ?? 0),
  );
  const categoriesDone = getNonDeciderChildren(sorted).every(
    (c) => c.status === TournamentMatchStatusEnum.FINISHED,
  );
  if (!categoriesDone) return false;
  const { homeWins, awayWins } = countCategoryWins(sorted);
  return homeWins === awayWins && homeWins > 0;
}
